import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { PortalUser, LoginResponse } from '../../shared/models/portal.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUser$ = new BehaviorSubject<PortalUser | null>(null);
  private apiUrl = environment.portalApiUrl;

  // In-memory token storage — avoids cookie timing issues
  tokenId: string = '';
  tokenHash: string = '';

  /** In-memory only — the user's password P for seamless launch. Never persisted; cleared on logout/refresh. */
  private sessionPassword: string | null = null;
  setSessionPassword(p: string): void { this.sessionPassword = p || null; }
  getSessionPassword(): string | null { return this.sessionPassword; }

  constructor(
    private http: HttpClient,
    private cookieService: CookieService,
  ) {
    this.loadFromCookie();
  }

  get user$(): Observable<PortalUser | null> {
    return this.currentUser$.asObservable();
  }

  get currentUser(): PortalUser | null {
    return this.currentUser$.value;
  }

  get isLoggedIn(): boolean {
    return !!this.currentUser;
  }

  get isGlobalAdmin(): boolean {
    return this.currentUser?.isGlobalAdmin || false;
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.apiUrl}/api/auth/login`, { email, password }).pipe(
      tap(response => {
        if (response.success) {
          // Store in memory (immediate, no cookie delay)
          this.tokenId = String(response.tokenId);
          this.tokenHash = response.tokenHash;
          this.currentUser$.next(response.user);
          this.setSessionPassword(password);

          // Also persist to cookies for page refresh survival
          this.cookieService.set('portalTokenId', this.tokenId, { path: '/' });
          this.cookieService.set('portalTokenHash', this.tokenHash, { path: '/' });
          this.cookieService.set('portalUser', JSON.stringify(response.user), { path: '/' });
        }
      })
    );
  }

  logout(): void {
    this.http.post(`${this.apiUrl}/api/auth/logout`, {}).subscribe({ error: () => {} });
    this.sessionPassword = null;
    this.tokenId = '';
    this.tokenHash = '';
    this.currentUser$.next(null);
    this.cookieService.delete('portalTokenId', '/');
    this.cookieService.delete('portalTokenHash', '/');
    this.cookieService.delete('portalUser', '/');
    this.cookieService.deleteAll('/');
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/forgot-password`, { email });
  }

  changePassword(token: string, oldPassword: string, newPassword: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/api/auth/change-password`, { token, oldPassword, newPassword });
  }

  private loadFromCookie(): void {
    // Restore session from cookies on page refresh
    const userJson = this.cookieService.get('portalUser');
    const tid = this.cookieService.get('portalTokenId');
    const thash = this.cookieService.get('portalTokenHash');
    if (userJson && tid) {
      try {
        this.currentUser$.next(JSON.parse(userJson));
        this.tokenId = tid;
        this.tokenHash = thash || '';
      } catch { /* invalid cookie */ }
    }
  }
}
